import Bugsnag from '@bugsnag/js';
import type { AxiosInstance, AxiosRequestConfig } from 'axios';
import { createHttp, http } from '@/http/axios';
import {
  browserCanaryStorage,
  isLiveApiEligibleAuthorization,
  isLiveApiFallbackError,
  parseCanaryPercent,
  readCanaryBucket,
  shouldUseLiveApi,
} from '@/lib/liveApiRouting';

export type PublicReadCanary = 'default' | 'offer';

const LIVE_API_URL = import.meta.env.VITE_LIVE_API_URL as string | undefined;
/** Les offres ont leur propre canary : à 0 (ou absent), elles restent sur welya-api. */
const CANARY_PERCENTS: Record<PublicReadCanary, number> = {
  default: parseCanaryPercent(import.meta.env.VITE_LIVE_API_CANARY_PERCENT as string | undefined),
  offer: parseCanaryPercent(
    import.meta.env.VITE_LIVE_API_OFFER_CANARY_PERCENT as string | undefined,
  ),
};
/** Court par rapport aux 20 s de welya-api : un live-api muet doit basculer vite sur le repli. */
const LIVE_API_TIMEOUT_MS = 5000;
const FALLBACK_REPORT_MESSAGE = 'Live API fallback';

const bugsnagApiKey = import.meta.env.VITE_BUGSNAG_API_KEY;
const isBugsnagEnabled = bugsnagApiKey !== undefined && bugsnagApiKey !== '';

let liveApiHttp: AxiosInstance | undefined;
const enabledCanaries = new Map<PublicReadCanary, boolean>();
let fallbackReported = false;

function resolveLiveApiHttp(canary: PublicReadCanary): AxiosInstance | null {
  let enabled = enabledCanaries.get(canary);
  if (enabled === undefined) {
    enabled = shouldUseLiveApi(LIVE_API_URL, CANARY_PERCENTS[canary], () =>
      readCanaryBucket(browserCanaryStorage()),
    );
    enabledCanaries.set(canary, enabled);
  }
  if (!enabled || !LIVE_API_URL) return null;
  liveApiHttp ??= createHttp(LIVE_API_URL, LIVE_API_TIMEOUT_MS);
  return liveApiHttp;
}

/** Un seul rapport par chargement de page : un incident live-api ne doit pas inonder Bugsnag. */
function reportFallback(path: string, error: unknown): void {
  if (!isBugsnagEnabled || fallbackReported) return;
  fallbackReported = true;
  const { status, code } = (error ?? {}) as { status?: number; code?: string };
  try {
    Bugsnag.notify(new Error(FALLBACK_REPORT_MESSAGE), (event) => {
      event.severity = 'warning';
      event.addMetadata('liveApiFallback', { path, status: status ?? null, code: code ?? null });
    });
  } catch {
    // le repli reste effectif même si le rapport échoue
  }
}

export type PublicReadOptions = {
  authorization?: string;
  params?: Record<string, string | number | boolean | undefined>;
  canary?: PublicReadCanary;
  /** Délais avant de relire live-api après un 424, avant tout repli sur welya-api. */
  liveApiRetryDelaysMs?: readonly number[];
};

export type PublicReadResponse<T> = {
  data: T;
  headers: Record<string, unknown>;
};

function requestConfig(options: PublicReadOptions): AxiosRequestConfig | undefined {
  if (!options.authorization && !options.params) return undefined;
  return {
    ...(options.authorization ? { headers: { Authorization: options.authorization } } : {}),
    ...(options.params ? { params: options.params } : {}),
  };
}

function get<T>(client: AxiosInstance, path: string, config: AxiosRequestConfig | undefined) {
  return config ? client.get<T>(path, config) : client.get<T>(path);
}

/**
 * Une route authentifiée (option `authorization` passée, même vide) ne part vers live-api qu'avec un
 * token d'inscription complet : un 401 de live-api viderait le contexte du spectateur.
 */
function resolveLiveApiFor(options: PublicReadOptions): AxiosInstance | null {
  if ('authorization' in options && !isLiveApiEligibleAuthorization(options.authorization)) {
    return null;
  }
  return resolveLiveApiHttp(options.canary ?? 'default');
}

function isLiveStateUnavailable(error: unknown): boolean {
  return (error as { status?: number } | null)?.status === 424;
}

function wait(delayMs: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, delayMs));
}

export function isServedByLiveApi(options: PublicReadOptions): boolean {
  return resolveLiveApiFor(options) !== null;
}

/** Lectures publiques servies par welya-live-api pendant le canary, avec repli sur welya-api. */
export async function getPublicReadResponse<T>(
  path: string,
  options: PublicReadOptions = {},
): Promise<PublicReadResponse<T>> {
  const config = requestConfig(options);
  const liveApi = resolveLiveApiFor(options);
  const retryDelaysMs = options.liveApiRetryDelaysMs ?? [];
  if (liveApi) {
    for (let attempt = 0; ; attempt++) {
      try {
        const response = await get<T>(liveApi, path, config);
        return { data: response.data, headers: response.headers };
      } catch (error) {
        const retryDelayMs = retryDelaysMs[attempt];
        if (retryDelayMs !== undefined && isLiveStateUnavailable(error)) {
          await wait(retryDelayMs);
          continue;
        }
        if (!isLiveApiFallbackError(error)) throw error;
        reportFallback(path, error);
        break;
      }
    }
  }
  const response = await get<T>(http, path, config);
  return { data: response.data, headers: response.headers };
}

export async function getPublicRead<T>(path: string, options: PublicReadOptions = {}): Promise<T> {
  return (await getPublicReadResponse<T>(path, options)).data;
}
